import { AfterViewInit, Component, effect, signal, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { fadeAnimation } from "src/app/animations/fade-animation";
import LocalizationConstants from "src/app/constants/localization-constants";
import { ColumnHeaderDefinition } from "src/app/models/ColumnHeaderDefinition";
import { TechCoverTicketStatus } from "src/app/models/Enums";
import { TechCoverTicket } from "src/app/models/TechCoverTicket";
import { TechCoverTicketsDataService } from "src/app/services/tech-cover-tickets-data.service";
import { commonModulesImports, materialCommonImports } from "src/app/utils/import.utils";

export type TechCoverTicketWithStatus = Omit<TechCoverTicket, "deserialize"> & {
  statusLabel: string;
  statusClass: string;
};

@Component({
  selector: "app-tech-cover-list",
  templateUrl: "./tech-cover-list.component.html",
  styleUrl: "./tech-cover-list.component.scss",
  standalone: true,
  imports: [...commonModulesImports, ...materialCommonImports],
  animations: [fadeAnimation],
})
export class TechCoverListComponent implements AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly localizationConstants = LocalizationConstants;
  readonly ticketStatus = TechCoverTicketStatus;

  dataSource = signal(new MatTableDataSource<TechCoverTicketWithStatus>());

  columns: ColumnHeaderDefinition[] = [
    { key: "caseNumber", title: LocalizationConstants.TECH_COVER_TICKET_NUMBER, type: "text" },
    { key: "subject", title: LocalizationConstants.TECH_COVER_TICKET_SUBJECT, type: "text" },
    {
      key: "productName",
      title: LocalizationConstants.TECH_COVER_TICKET_PRODUCT_NAME,
      type: "text",
    },
    { key: "status", title: LocalizationConstants.TECH_COVER_TICKET_STATUS, type: "text" },
    {
      key: "createdDate",
      title: LocalizationConstants.TECH_COVER_TICKET_CREATED_DATE,
      type: "date",
    },
    {
      key: "createdBy",
      title: LocalizationConstants.TECH_COVER_TICKET_CREATED_BY,
      type: "text",
    },
    { key: "closedDate", title: LocalizationConstants.TECH_COVER_TICKET_CLOSED_DATE, type: "date" },
  ];

  columnKeys = this.columns.map((c) => c.key);

  constructor(
    private router: Router,
    private techCoverTicketsDataService: TechCoverTicketsDataService,
  ) {
    effect(() => {
      const tickets = this.techCoverTicketsDataService.supportTickets();
      this.setSupportTickets(tickets);
    });
  }

  ngAfterViewInit(): void {
    this.updateDataSourceSortAndPaging();
  }

  private updateDataSourceSortAndPaging(): void {
    this.dataSource.update((dataSource) => {
      dataSource.sort = this.sort;
      dataSource.paginator = this.paginator;
      return dataSource;
    });
  }

  private getTicketStatusText(status: TechCoverTicketStatus): string {
    switch (status) {
      case TechCoverTicketStatus.Closed:
        return this.localizationConstants.TECH_COVER_TICKET_CLOSED_STATUS;

      case TechCoverTicketStatus.InProgress:
        return this.localizationConstants.TECH_COVER_TICKET_IN_PROGRESS_STATUS;

      case TechCoverTicketStatus.New:
        return this.localizationConstants.TECH_COVER_TICKET_NEW_STATUS;

      default:
        new Error(`Status (${status}) is unknown and cannot be mapped.`);
    }
  }

  private getTicketStatusClass(status: TechCoverTicketStatus): string {
    switch (status) {
      case TechCoverTicketStatus.Closed:
        return "closed";

      case TechCoverTicketStatus.InProgress:
        return "in-progress";

      case TechCoverTicketStatus.New:
        return "new";

      default:
        new Error(`Status (${status}) is unknown and cannot be mapped.`);
    }
  }

  public setSupportTickets(tickets: TechCoverTicket[]): void {
    this.dataSource.update((dataSource) => {
      dataSource.data = tickets
        .sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime())
        .map((ticket) => {
          return {
            ...ticket,
            statusLabel: this.getTicketStatusText(ticket.status),
            statusClass: this.getTicketStatusClass(ticket.status),
          };
        });
      return dataSource;
    });
  }

  public onNewTicketClick(): void {
    this.router.navigate(["/tech-cover/create-ticket"]);
  }
}
